import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Button, Link } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { validate as isValidEmail } from "email-validator";
import { type FormEvent, useRef, useState } from "react";

import { LoginStepHeading } from "./LoginStepHeading";
import { OrDivider } from "./OrDivider";

const TERMS_OF_SERVICE_URL = "https://www.clipboardworks.com/terms-of-service";
const PRIVACY_POLICY_URL = "https://www.clipboardworks.com/privacy-policy";

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  loading: boolean;
  onSubmit: () => Promise<void>;
  onSwitchToPassword?: () => void;
  onSsoClick?: () => Promise<void>;
  showSsoButton?: boolean;
  isSsoEmail?: boolean;
  verifyingEmail?: boolean;
  /** Overrides the primary button label (e.g. for the email-first step). */
  submitLabelOverride?: string;
  disabled?: boolean;
  errorMessage?: string;
  showPasskeyButton?: boolean;
  passkeyLoading?: boolean;
  passkeyError?: string;
  onPasskeyClick?: () => void;
  onPasskeyInvalidEmail?: () => void;
  /** Uses the semantic step-heading system for the redesigned email-first screen. */
  useStepHeading?: boolean;
}

/**
 * Login form with magic link
 */
export function LoginForm(props: LoginFormProps) {
  const { tu } = useTu("login");

  const {
    email,
    setEmail,
    loading,
    onSubmit,
    onSwitchToPassword,
    onSsoClick,
    showSsoButton = false,
    isSsoEmail = false,
    verifyingEmail = false,
    submitLabelOverride,
    disabled = false,
    errorMessage,
    showPasskeyButton = false,
    passkeyLoading = false,
    passkeyError,
    onPasskeyClick,
    onPasskeyInvalidEmail,
    useStepHeading = false,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const isEmailValid = isValidEmail(email.trim());

  function handlePasskeyButtonClick() {
    // Always enabled. If the email isn't valid yet, surface an error and focus
    // the field instead of attempting sign-in. Otherwise call onPasskeyClick
    // synchronously so the WebAuthn ceremony stays inside the user gesture.
    if (!isEmailValid) {
      onPasskeyInvalidEmail?.();
      emailInputRef.current?.focus();
      return;
    }
    onPasskeyClick?.();
  }

  const formLabel = verifyingEmail
    ? tu("Please enter your email address to verify login link")
    : tu("Enter your email to get started");

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  function getSubmitLabel(): string {
    if (verifyingEmail) {
      return tu("Verify Email");
    }

    if (isSsoEmail) {
      return tu("Sign in with SSO");
    }

    return submitLabelOverride ?? tu("Get Sign-in Link");
  }

  const submitLabel = getSubmitLabel();

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      {useStepHeading ? (
        <LoginStepHeading>{formLabel}</LoginStepHeading>
      ) : (
        <Text variant="body1" sx={{ textAlign: "center", color: "base.muted" }}>
          {formLabel}
        </Text>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Text semibold variant="body2" sx={{ color: "base.muted" }}>
          {tu("Email")}
        </Text>
        <TextInput
          fullWidth
          required
          type="email"
          name="email"
          id="login-email"
          placeholder="your.email@example.com"
          value={email}
          inputRef={emailInputRef}
          disabled={disabled || loading}
          autoComplete="email"
          inputProps={{ "aria-label": tu("Email") }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1rem",
              backgroundColor: "background.standout",
            },
          }}
          onInput={(event) => {
            setEmail((event.target as HTMLInputElement).value);
          }}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </Box>
      {showSsoButton ? (
        <Box sx={{ display: "flex", gap: 2 }}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="primary"
            size="large"
            isLoading={isSubmitting || loading}
            disabled={disabled || passkeyLoading}
          >
            {submitLabel}
          </LoadingButton>
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={isSsoLoading}
            disabled={disabled || isSubmitting || loading || isSsoLoading || passkeyLoading}
            onClick={(event) => {
              event.preventDefault();
              setIsSsoLoading(true);
              onSsoClick?.()?.finally(() => {
                setIsSsoLoading(false);
              });
            }}
          >
            {tu("Sign in with SSO")}
          </LoadingButton>
        </Box>
      ) : (
        <>
          <LoadingButton
            fullWidth
            type="submit"
            variant="primary"
            size="large"
            isLoading={isSubmitting || loading}
            disabled={disabled || passkeyLoading}
          >
            {submitLabel}
          </LoadingButton>
        </>
      )}
      {errorMessage && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {errorMessage}
        </Text>
      )}
      {!verifyingEmail && (
        <Text variant="body2" sx={{ textAlign: "center", color: "base.muted" }}>
          {tu('By clicking "{{action}}" you agree to Clipboard\'s', {
            values: { action: submitLabel },
          })}{" "}
          <Link
            href={TERMS_OF_SERVICE_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {tu("Terms of Service")}
          </Link>{" "}
          {tu("and acknowledge that you have read our")}{" "}
          <Link
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
          >
            {tu("Privacy Policy")}
          </Link>
          .
        </Text>
      )}
      {/* Passkey (WebAuthn) sign-in — placed directly below the primary action.
          Always enabled: handlePasskeyButtonClick focuses the email field when
          the email isn't valid yet. */}
      {!verifyingEmail && showPasskeyButton && onPasskeyClick && (
        <>
          <OrDivider />
          <LoadingButton
            fullWidth
            type="button"
            variant="primary"
            size="large"
            isLoading={passkeyLoading}
            disabled={disabled}
            onClick={handlePasskeyButtonClick}
          >
            {tu("Sign in with a passkey")}
          </LoadingButton>
          {passkeyError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {passkeyError}
            </Text>
          )}
        </>
      )}
      {/* The password-login switch isn't meaningful in the email-verify context,
          where there's no pending login session to switch the method for. */}
      {!verifyingEmail && onSwitchToPassword && (
        <>
          <OrDivider />
          {/* Alternative login option */}
          <Button
            variant="text"
            disabled={disabled || loading}
            sx={{
              textTransform: "none",
              fontSize: "1rem",
              color: "primary.main",
              alignSelf: "center",
            }}
            onClick={onSwitchToPassword}
          >
            {tu("Sign in with email & password")}
          </Button>
        </>
      )}
    </Box>
  );
}
